import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import examples from 'libphonenumber-js/mobile/examples';
import { Observable, startWith, Subscription } from 'rxjs';
import { FormControl, FormGroup, FormGroupDirective, ReactiveFormsModule, Validators } from '@angular/forms';
import { map } from 'rxjs/operators';
import {
  CountryCode,
  getExampleNumber,
  parsePhoneNumberWithError,
  PhoneNumber,
  validatePhoneNumberLength,
} from 'libphonenumber-js';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { DropDownCountry, PhoneUtils } from '../services/phoneUtils';
import { NumberPlusDirective } from '../directives/numberPlusDirective';

export const intlPhoneErrorMessagesMap = {
  REQUIRED: 'Phone number is mandatory',
  INVALID: 'Invalid phone number',
  INVALID_LENGTH: 'Invalid phone number',
  TOO_LONG: 'Phone number is too long',
  TOO_SHORT: 'Phone number is too short',
};

@Component({
  selector: 'common-intl-phone-number',
  templateUrl: './intl-phone-number.component.html',
  styleUrls: ['./intl-phone-number.component.scss'],
  imports: [ReactiveFormsModule, MatInputModule, NumberPlusDirective, AsyncPipe, NgForOf, NgIf, MatAutocompleteModule],
  standalone: true,
})
export class IntlPhoneNumberComponent implements OnInit, OnDestroy {
  @ViewChild('trigger') autoCompleteTrigger?: MatAutocompleteTrigger;
  @Input() isCountryDisabled?: boolean = false;
  @Input() disabled?: boolean = false;
  @Input({ required: true }) controlName!: string;
  @Output() emitBlur = new EventEmitter();
  form!: FormGroup;
  intlPhoneErrorMessagesMap = intlPhoneErrorMessagesMap;
  filteredCountries?: Observable<DropDownCountry[]>;
  countries?: DropDownCountry[] = [];

  isOpen = false;
  selectedCountry: DropDownCountry = {
    phonePrefix: '+44',
    flagClass: 'fi fi-gb',
    code: 'GB',
  };

  formSub?: Subscription;
  countrySub?: Subscription;
  numberSub?: Subscription;

  constructor(
    private phoneUtils: PhoneUtils,
    private rootFormGroup: FormGroupDirective,
  ) {}

  ngOnInit() {
    this.countries = this.phoneUtils.getAllCountriesData();
    this.form = this.rootFormGroup.form.controls[this.controlName] as FormGroup;
    this.addInternalControls();
    if (this.form.get('phone')?.value && typeof this.form.get('phone')?.value === 'string') {
      this.form
        .get('numberWithoutPrefixForDisplay')
        ?.setValue(this.form.get('phone')!.value.replace(this.form.get('country')?.value || '', ''));
      this.parseValidateDisplayIntlNumber(this.form.get('phone')?.value);
    }
    if (this.disabled) {
      this.form.get('country')?.disable();
      this.form.get('numberWithoutPrefixForDisplay')?.disable();
    }
    this.formSub = this.form.get('phone')?.valueChanges?.subscribe((value) => {
      if (!value) this.form.get('numberWithoutPrefixForDisplay')?.setValue('');
      this.parseValidateDisplayIntlNumber(this.form.get('phone')?.value);
    });
    this.numberSub = this.form.get('numberWithoutPrefixForDisplay')?.valueChanges.subscribe((value) => {
      if (value === null) value = '';
      if (value?.startsWith('00')) {
        value = value.replace('00', '+');
      }
      if (value?.startsWith('+') && this.isCountryDisabled) {
        value = value.replace('+', '');
      }
      const countryValue = value?.startsWith('+') ? '' : this.selectedCountry?.phonePrefix;
      const valueToParse = countryValue + (value?.startsWith(countryValue) ? value.replace(countryValue, '') : value);
      this.parseValidateDisplayIntlNumber(valueToParse);
    });
  }

  ngOnDestroy() {
    this.formSub?.unsubscribe();
    this.countrySub?.unsubscribe();
    this.numberSub?.unsubscribe();
  }

  addInternalControls() {
    this.form.addControl('country', new FormControl('+44', Validators.required));
    this.form.addControl('numberWithoutPrefixForDisplay', new FormControl('', Validators.required));
    this.filteredCountries = this.form?.get('country')?.valueChanges.pipe(
      startWith(''),
      map((country) => (country ? this._filterCountries(country) : this.countries?.slice())),
    );
  }

  //Form events
  blurOutput(sendEvent = true) {
    this.closeMenuIfNotFocused();
    if (sendEvent) this.emitBlur.emit();
  }
  onClosing() {
    this.closeMenuIfNotFocused();
    this.isOpen = false;
  }

  closeMenuIfNotFocused() {
    setTimeout(() => {
      if (document.querySelector(':focus')?.id !== 'countries-search') {
        this.autoCompleteTrigger?.closePanel();
      }
    }, 200);
  }
  onOpenDD() {
    this.form?.get('country')?.setValue(this.form?.get('country')?.value);
    this.isOpen = true;
    setTimeout(() => {
      this.autoCompleteTrigger?.['_element']?.nativeElement?.select();
    }, 0);
  }
  onCloseDD(e: HTMLInputElement) {
    e.focus();
    this.isOpen = false;
  }
  onOptionSelected(event: any) {
    event?.option?._element?.nativeElement?.click();
  }
  selectCountry(country: DropDownCountry) {
    this.selectedCountry = country;
    this.setPlaceHolder();
    const phoneInputValue = this.form.get('numberWithoutPrefixForDisplay')?.value;
    const valueToParse =
      this.selectedCountry.phonePrefix +
      (phoneInputValue?.startsWith(this.selectedCountry.phonePrefix)
        ? phoneInputValue.replace(this.selectedCountry.phonePrefix, '')
        : phoneInputValue);
    this.parseValidateDisplayIntlNumber(valueToParse, country?.code);
  }

  setPlaceHolder() {
    if (this.selectedCountry!.code) {
      try {
        this.selectedCountry!.placeHolder = getExampleNumber(this.selectedCountry!.code!, examples)?.formatNational();
      } catch (e) {
        console.error(e);
      }
    }
  }
  setSelectedCountry(parsedObject?: PhoneNumber | { country: CountryCode; countryCallingCode: string }) {
    if (parsedObject) {
      this.selectedCountry = {
        phonePrefix: '+' + parsedObject.countryCallingCode,
        flagClass:
          this.phoneUtils.getExceptionFlag(parsedObject?.country?.toLowerCase()) ||
          'fi fi-' + (parsedObject?.country?.toLowerCase() || 'gb'),
        code: parsedObject?.country,
      };
    } else {
      this.selectedCountry = {
        phonePrefix: '+44',
        flagClass: 'fi fi-gb',
        code: 'GB',
      };
    }
  }
  private _filterCountries(value: string): any {
    if (typeof value !== 'string') return;
    const filterValue = value.toLowerCase();
    const result = this.countries?.filter(
      (country) =>
        country.name?.toLowerCase().includes(filterValue) ||
        country.phonePrefix?.toLowerCase().includes(filterValue) ||
        country.code?.toLowerCase().includes(filterValue),
    );
    return result && result.length > 0 ? result : [{ flagClass: 'no-result' }];
  }

  //Parsing & Validating
  parseValidateDisplayIntlNumber(number: string, defaultCountry?: CountryCode) {
    if (!number) return this.handleInvalidNumber('');
    try {
      const parsedNumberFromLib = parsePhoneNumberWithError(number, defaultCountry);
      if (!defaultCountry && parsedNumberFromLib.isValid()) {
        this.setSelectedCountry(parsedNumberFromLib);
      }
      if (parsedNumberFromLib.isValid()) {
        const newCode = '+' + (parsedNumberFromLib.countryCallingCode || '44');
        if (newCode !== this.form.get('country')?.value) {
          this.form.patchValue({
            country: newCode,
            numberWithoutPrefixForDisplay:
              parsedNumberFromLib.nationalNumber || this.form.get('phone')?.value?.replace('+44', ''),
          });
        }
        const newNumber = parsedNumberFromLib.nationalNumber || this.form.get('phone')?.value?.replace('+44', '');
        if (newNumber !== this.form.get('numberWithoutPrefixForDisplay')?.value) {
          this.form.patchValue({
            numberWithoutPrefixForDisplay: newNumber,
          });
        }
        this.handleValidNumber(parsedNumberFromLib.number);
      } else {
        this.handleInvalidNumber(parsedNumberFromLib.number || number);
      }
    } catch (e: any) {
      this.handleInvalidNumber(number);
    }
  }

  handleValidNumber(number: string) {
    if (number !== this.form.get('phone')?.value) {
      this.form.get('phone')?.setValue(number);
    }
    this.form.get('phone')?.setErrors(null);
  }

  handleInvalidNumber(number = '') {
    let errorCode = 'INVALID';
    try {
      errorCode = validatePhoneNumberLength(number) || errorCode;
    } catch (e) {
      errorCode = 'INVALID';
    }
    const error = !number ? 'REQUIRED' : errorCode;
    if (!this.form.get('phone')?.hasError(error)) {
      this.form.get('phone')?.setErrors({ [error]: true });
    }
    if (number !== this.form.get('phone')?.value) {
      this.form.get('phone')?.setValue(number);
    }
  }
}
